<template>
  <v-container fluid class="page-container">
    <div class="header-section mb-6">
      <div class="d-flex align-center">
        <v-icon large color="primary" class="mr-4">mdi-chart-areaspline</v-icon>
        <div>
          <h1 class="text-h4 font-weight-bold primary--text">Análisis Comercial</h1>
          <p class="text-subtitle-1 grey--text text--darken-1">Reportes de rentabilidad y flujo de mercadería</p>
        </div>
      </div>
      <v-spacer></v-spacer>
      <div class="date-picker-group d-flex align-center">
        <v-text-field
          v-model="dates.start"
          type="date"
          label="Desde"
          outlined
          dense
          hide-details
          class="mr-2 rounded-lg"
        ></v-text-field>
        <v-text-field
          v-model="dates.end"
          type="date"
          label="Hasta"
          outlined
          dense
          hide-details
          class="mr-4 rounded-lg"
        ></v-text-field>
        <v-btn color="primary" @click="loadData" :loading="loading" elevation="2" class="rounded-lg">
          <v-icon left>mdi-refresh</v-icon> Actualizar
        </v-btn>
      </div>
    </div>

    <v-row>
      <!-- KPI Cards -->
      <v-col cols="12" md="3">
        <v-card elevation="1" class="rounded-xl pa-4 kpi-card kpi-card--revenue">
          <div class="text-overline mb-1">FACTURACIÓN TOTAL</div>
          <div class="text-h4 font-weight-black">$ {{ formatNumber(metrics.revenue) }}</div>
          <div class="kpi-caption">Ventas emitidas en el período</div>
          <v-icon class="kpi-icon">mdi-currency-usd</v-icon>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card elevation="1" class="rounded-xl pa-4 kpi-card kpi-card--cost">
          <div class="text-overline mb-1">COSTO DE MERCADERÍA</div>
          <div class="text-h4 font-weight-black">$ {{ formatNumber(metrics.cost) }}</div>
          <div v-if="metrics.hasIncompleteCost" class="kpi-caption kpi-caption--warning">
            {{ formatNumber(metrics.unitsWithoutCost) }} unidades sin costo cargado
          </div>
          <div v-else class="kpi-caption">Costo informado para las ventas</div>
          <v-icon class="kpi-icon">mdi-truck-delivery-outline</v-icon>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card elevation="1" class="rounded-xl pa-4 kpi-card kpi-card--profit">
          <div class="text-overline mb-1">UTILIDAD BRUTA</div>
          <div class="text-h4 font-weight-black">$ {{ formatNumber(metrics.profit) }}</div>
          <div v-if="metrics.hasIncompleteCost" class="kpi-caption kpi-caption--warning">Estimación: faltan costos de productos</div>
          <div v-else class="kpi-caption">Facturación menos costo de mercadería</div>
          <v-icon class="kpi-icon">mdi-trending-up</v-icon>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card elevation="1" class="rounded-xl pa-4 kpi-card kpi-card--margin">
          <div class="text-overline mb-1">MARGEN DE GANANCIA</div>
          <div class="text-h4 font-weight-black">{{ marginLabel }}</div>
          <div v-if="metrics.hasIncompleteCost" class="kpi-caption kpi-caption--warning">No concluyente hasta cargar costos</div>
          <div v-else class="kpi-caption">Sobre facturación del período</div>
          <v-icon class="kpi-icon">mdi-percent</v-icon>
        </v-card>
      </v-col>

      <!-- Charts Row -->
      <v-col cols="12" md="7">
        <v-card elevation="2" class="rounded-xl pa-6 fill-height">
          <div class="d-flex align-center mb-6">
            <div>
              <h3 class="text-h6 font-weight-bold grey--text text--darken-3">Ventas facturadas vs compras</h3>
              <div class="text-caption grey--text">Comparación comercial; no representa cobros y pagos reales.</div>
            </div>
          </div>
          <apexchart
            v-if="!loading"
            type="bar"
            height="350"
            :options="cashFlowChartOptions"
            :series="cashFlowSeries"
          ></apexchart>
          <div v-else class="d-flex justify-center align-center" style="height: 350px">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" md="5">
        <v-card elevation="2" class="rounded-xl pa-6 fill-height">
          <div class="d-flex align-center mb-6">
            <h3 class="text-h6 font-weight-bold grey--text text--darken-3">Top 5 Productos por Ingresos</h3>
          </div>
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">Producto</th>
                  <th class="text-right">Unidades</th>
                  <th class="text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in topProducts" :key="item.name">
                  <td>{{ item.name }}</td>
                  <td class="text-right">{{ item.totalQty }}</td>
                  <td class="text-right font-weight-bold primary--text">$ {{ formatNumber(item.totalRevenue) }}</td>
                </tr>
                <tr v-if="!topProducts.length"><td colspan="3" class="text-center grey--text py-5">No hay ventas en el período seleccionado.</td></tr>
              </tbody>
            </template>
          </v-simple-table>
          
          <div class="mt-6">
             <apexchart
              v-if="!loading"
              type="donut"
              height="250"
              :options="donutChartOptions"
              :series="donutSeries"
            ></apexchart>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top>
      {{ snackbar.text }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { reportingService } from "../services/reporting";

export default {
  name: "CommercialReports",
  data() {
    return {
      loading: false,
      dates: {
        start: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
        end: new Date().toISOString().split('T')[0]
      },
      metrics: {
        revenue: 0,
        cost: 0,
        profit: 0,
        marginPercent: 0
      },
      cashFlow: {
        salesTotal: 0,
        purchasesTotal: 0
      },
      topProducts: [],
      snackbar: { show: false, text: "", color: "success" },
      cashFlowChartOptions: {
        chart: { id: 'cash-flow-bar', toolbar: { show: false } },
        colors: ['#4CAF50', '#FF9800'],
        plotOptions: { bar: { borderRadius: 8, columnWidth: '50%' } },
        xaxis: { categories: ['Ventas', 'Compras'] },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: (val) => `$ ${val.toLocaleString()}` } }
      },
      donutChartOptions: {
        labels: [],
        colors: ['#3f51b5', '#00bcd4', '#4caf50', '#ffeb3b', '#ff9800'],
        legend: { position: 'bottom' },
        dataLabels: { enabled: true, formatter: (val) => `${val.toFixed(0)}%` }
      }
    };
  },
  computed: {
    cashFlowSeries() {
      return [{
        name: 'Monto',
        data: [this.cashFlow.salesTotal, this.cashFlow.purchasesTotal]
      }];
    },
    donutSeries() {
      return this.topProducts.map(p => Number(p.totalRevenue));
    },
    marginLabel() {
      return this.metrics.hasIncompleteCost ? 'Pendiente' : `${this.formatNumber(this.metrics.marginPercent)} %`;
    }
  },
  async created() {
    await this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        const [profitRes, cashFlowRes, topRes] = await Promise.all([
          reportingService.getGrossProfit(this.dates.start, this.dates.end),
          reportingService.getCashFlow(this.dates.start, this.dates.end),
          reportingService.getTopProducts(5, this.dates.start, this.dates.end)
        ]);

        this.metrics = profitRes.data.data;
        this.cashFlow = cashFlowRes.data.data;
        this.topProducts = topRes.data.data;
        
        this.donutChartOptions = {
          ...this.donutChartOptions,
          labels: this.topProducts.map(p => p.name)
        };

      } catch (e) {
        this.metrics = { revenue: 0, cost: 0, profit: 0, marginPercent: null, soldUnits: 0, unitsWithoutCost: 0, hasIncompleteCost: false };
        this.cashFlow = { salesTotal: 0, purchasesTotal: 0 };
        this.topProducts = [];
        this.showSnackbar(
          e?.response?.data?.errors || e?.response?.data?.error || "No se pudieron cargar los reportes",
          "error"
        );
      } finally {
        this.loading = false;
      }
    },
    formatNumber(val) {
      return Number(val || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    showSnackbar(text, color = "success") {
      this.snackbar = { show: true, text, color };
    }
  }
};
</script>

<style scoped>
.page-container {
  background-color: #f8fafc;
  min-height: 100vh;
  padding: 24px;
}
.kpi-card {
  position: relative;
  overflow: hidden;
  min-height: 130px;
  background: #fff !important;
  border: 1px solid #e2e8f0;
  border-left: 5px solid var(--kpi-accent);
  color: #1e293b;
}
.kpi-card--revenue { --kpi-accent: #2563eb; }
.kpi-card--cost { --kpi-accent: #d97706; }
.kpi-card--profit { --kpi-accent: #0f766e; }
.kpi-card--margin { --kpi-accent: #7c3aed; }
.kpi-caption {
  position: relative;
  z-index: 1;
  margin-top: 6px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.25;
}
.kpi-caption--warning { color: #b45309; font-weight: 600; }
.kpi-icon {
  position: absolute;
  right: -10px;
  bottom: -10px;
  font-size: 80px !important;
  color: var(--kpi-accent) !important;
  opacity: 0.12;
}

.bg-light-grey { background-color: #f1f5f9; }
</style>
